import React, { useCallback, useState } from "react";
import { ChevronDown } from "react-feather";
import type { ControlSizeKey } from "../../../design-system/tokens/controlSizes";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import type { PortalMenuRect } from "../../../hooks/usePortalMenu";
import { KgCalendarIcon } from "../../atoms/KgCalendarIcon/KgCalendarIcon";
import { Calendar } from "../Calendar";
import { Wrap, IconSlot, Value } from "./DateField.styles";

/**
 * Hands the calendar's open state and anchors to the parent, the way
 * `Dropdown` takes them: needed inside another portal menu, whose outside-click
 * check must count the calendar as inside (`containedRefs`). The parent closes
 * it in `onChange`. Without these the field keeps its own state.
 */
export interface DateFieldPopoverProps {
  open: boolean;
  onToggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLDivElement>;
  menuRect: PortalMenuRect | null;
}

export interface DateFieldProps {
  /** ISO yyyy-mm-dd string (empty/null means not selected). */
  value: string;
  onChange: (value: string) => void;
  /** ISO min date. */
  min?: string;
  /** ISO max date. */
  max?: string;
  placeholder?: string;
  invalid?: boolean;
  size?: ControlSizeKey;
  ariaLabel?: string;
  /** Override the display formatter (defaults to "DDth Mon, YYYY"). */
  formatDisplay?: (iso: string) => string;
  /** Figma schedule fields use a single trailing calendar icon. */
  iconLayout?: "default" | "calendar-end";
  /** Visual-only inactive state used by disabled recurrence rows. */
  muted?: boolean;
  /** `sentence` sizes the trigger to sit inside a sentence (InlinePickTrigger's look); default `field`. */
  appearance?: "field" | "sentence";
  /** Parent-owned calendar state; see `DateFieldPopoverProps`. */
  popover?: DateFieldPopoverProps;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ordinalSuffix = (day: number): string => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const defaultFormatDisplay = (iso: string): string => {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return iso;
  return `${d}${ordinalSuffix(d)} ${MONTHS[m - 1]}, ${y}`;
};

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  min,
  max,
  placeholder = "Select a date",
  invalid = false,
  size = "md",
  ariaLabel,
  formatDisplay = defaultFormatDisplay,
  iconLayout = "default",
  muted = false,
  appearance = "field",
  popover,
}) => {
  const [ownOpen, setOpen] = useState(false);
  const displayValue = value ? formatDisplay(value) : placeholder;

  const close = useCallback(() => setOpen(false), []);

  // Kept closed when the parent owns the calendar, so only one hook positions it.
  const ownMenu = usePortalMenu<
    HTMLButtonElement,
    HTMLDivElement
  >({
    open: !popover && ownOpen,
    onOutsideClick: close,
  });
  const open = popover ? popover.open : ownOpen;
  const triggerRef = popover?.triggerRef ?? ownMenu.triggerRef;
  const menuRef = popover?.menuRef ?? ownMenu.menuRef;
  const rect = popover ? popover.menuRect : ownMenu.rect;

  const ownToggle = useCallback(() => setOpen((o) => !o), []);
  const toggle = popover?.onToggle ?? ownToggle;

  const handleSelect = useCallback(
    (iso: string) => {
      onChange(iso);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <>
      <Wrap
        ref={triggerRef}
        type="button"
        $invalid={invalid}
        $size={size}
        $muted={muted}
        $sentence={appearance === "sentence"}
        $open={open}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ?? "Select date"}
        aria-invalid={invalid || undefined}
        onClick={toggle}
      >
        {iconLayout === "default" && (
          <IconSlot $muted={muted}>
            <KgCalendarIcon size={16} />
          </IconSlot>
        )}
        <Value $placeholder={!value} $muted={muted} $sentence={appearance === "sentence"}>
          {displayValue}
        </Value>
        <IconSlot $muted={muted}>
          {iconLayout === "calendar-end" ? (
            <KgCalendarIcon size={16} />
          ) : (
            <ChevronDown size={16} color="currentColor" />
          )}
        </IconSlot>
      </Wrap>
      <Calendar
        value={value}
        onSelect={handleSelect}
        min={min}
        max={max}
        open={open}
        menuRef={menuRef}
        menuRect={rect}
      />
    </>
  );
};

export default DateField;
